import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_URL, CITIES, scrollToId } from "@/constants/brand";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className }) => (
  <div className="overflow-hidden py-0.5">
    <motion.div
      className={className}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  </div>
);

export const HeroSection = () => {
  const { scrollY } = useScroll();
  const logoY = useTransform(scrollY, [0, 700], [0, 140]);
  const glowY = useTransform(scrollY, [0, 700], [0, -80]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16 px-5 sm:px-8"
    >
      <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none">
        <div className="hero-glow w-[420px] h-[420px] bg-brand/25 -top-32 -right-32" />
        <div className="hero-glow w-[360px] h-[360px] bg-brand-deep/20 bottom-0 -left-40" />
      </motion.div>

      <div className="relative max-w-6xl mx-auto w-full grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 border border-brand/40 bg-card2/60 backdrop-blur-md rounded-full px-4 py-2 mb-8"
            data-testid="hero-badge"
          >
            <Zap className="w-3.5 h-3.5 text-brand" />
            <span className="text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase text-muted2">
              Recruitment Rider — Aperte le candidature
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25} className="text-white">
              BENVENUTO IN
            </MaskedLine>
            <MaskedLine delay={0.38} className="text-white">
              IO AMO LA
            </MaskedLine>
            <MaskedLine
              delay={0.51}
              className="italic text-transparent bg-clip-text bg-gradient-to-r from-brand-hot via-brand to-brand-deep"
            >
              MIA CITTÀ
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 text-base sm:text-lg text-muted2 leading-relaxed max-w-xl"
          >
            <span className="text-white font-bold">Lavora con noi!</span> Diventa Responsabile delle
            Consegne: ottimi guadagni, massima flessibilità e un team di persone vere al tuo fianco.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-apply"
              onClick={() => scrollToId("#candidati")}
              className="group inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hot text-white font-display font-bold text-base px-8 py-4 rounded-full transition-colors duration-300 shadow-[0_10px_30px_-5px_rgba(232,67,147,0.45)]"
            >
              Invia Candidatura Ora
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-benefits"
              onClick={() => scrollToId("#vantaggi")}
              className="inline-flex items-center justify-center gap-2 border border-brand/40 hover:border-brand hover:bg-card2 text-white font-display font-bold text-base px-8 py-4 rounded-full transition-colors duration-300"
            >
              Scopri i Vantaggi
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3"
            data-testid="hero-cities"
          >
            {CITIES.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-dim">
                <MapPin className="w-3.5 h-3.5 text-brand" /> {c}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: logoY }} className="relative hidden lg:flex items-center justify-center">
          <div className="hero-glow w-72 h-72 bg-brand/35 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          <motion.div
            animate={{ y: [0, -16, 0], rotate: [0, 2.5, 0] }}
            transition={{ repeat: Infinity, duration: 6.5, ease: "easeInOut" }}
            className="relative"
          >
            <motion.img
              src={LOGO_URL}
              alt="Io Amo La Mia Città — Toro"
              data-testid="hero-logo"
              initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
              className="relative w-72 h-72 xl:w-80 xl:h-80 object-cover rounded-[3rem] ring-1 ring-brand/50 shadow-[0_30px_80px_-20px_rgba(232,67,147,0.5)]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
            className="absolute -bottom-6 -left-4 bg-card2/90 backdrop-blur-md border border-brand/30 rounded-2xl px-5 py-4"
            data-testid="hero-stat-badge"
          >
            <p className="font-display font-black text-2xl text-brand">100%</p>
            <p className="text-xs font-semibold text-muted2">Gestione umana, zero algoritmi</p>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown className="w-5 h-5 text-dim" />
        </motion.div>
      </motion.div>
    </section>
  );
};
